import { useEffect, useMemo, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, resolveImage } from "@/lib/api";
import ArkHeader from "@/components/ArkHeader";
import ArkFooter from "@/components/ArkFooter";
import Swatch from "@/components/Swatch";
import { trackOnce, trackEvent } from "@/lib/tracking";
import { toast } from "sonner";
import { Check, ArrowLeft, ArrowRight, Sparkles, Loader2 } from "lucide-react";

const STEPS = [
  { k: "type", n: "01", l: "Choose a finish style" },
  { k: "color", n: "02", l: "Pick your colour" },
  { k: "hardware", n: "03", l: "Choose hardware" },
  { k: "contact", n: "04", l: "Enter your details to secure this piece" },
];

export default function PieceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const [palette, setPalette] = useState({
    stains: [],
    paints: [],
    hardware: [],
    sheens: ["Satin", "Semi-Gloss"],
  });

  const [step, setStep] = useState(0);
  const [finishType, setFinishType] = useState(null); // 'stain' | 'paint' | 'original'
  const [finishIdx, setFinishIdx] = useState(null);
  const [hardware, setHardware] = useState(null);
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // AI preview state
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const previewTimer = useRef(null);
  const previewAbort = useRef(0);

  useEffect(() => {
    trackOnce(`/piece/${id}`);
    api
      .get(`/furniture/${id}`)
      .then((r) => setItem(r.data))
      .catch(() => navigate("/"));
    api.get("/palette").then((r) => setPalette(r.data));
  }, [id, navigate]);

  // Track each wizard step the customer reaches (skip the thank-you state)
  useEffect(() => {
    if (step >= STEPS.length) return;
    trackEvent("wizard_step", { piece_id: id, step });
  }, [id, step]);

  const activeList = useMemo(
    () => (finishType === "stain" ? palette.stains : palette.paints),
    [finishType, palette],
  );
  const activeFinish = finishIdx != null ? activeList[finishIdx] : null;

  // Resolve which price to show based on the finish the customer has picked.
  // Falls back to whatever is available so the UI never shows a blank.
  const currentPrice = useMemo(() => {
    if (!item) return null;
    const base = item.base_price;
    if (finishType === "stain") return item.stain_price ?? base ?? null;
    if (finishType === "paint") return item.paint_price ?? base ?? null;
    if (finishType === "original") return base ?? null;
    return base ?? null;
  }, [item, finishType]);

  const formatPrice = (n) => {
    if (n == null || Number.isNaN(Number(n))) return null;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(Number(n));
  };

  // Auto-generate AI preview whenever the customer has enough selections.
  // Debounced 500ms; each run has a request id so stale results are dropped.
  useEffect(() => {
    // Nothing to preview before they've picked a finish type.
    if (!finishType) {
      setPreviewUrl(null);
      return;
    }
    // "Keep Original" → always show the real photo, never call the AI.
    if (finishType === "original") {
      setPreviewUrl(null);
      setShowPreview(false);
      return;
    }
    // Skip if no real colour chosen yet
    if (!activeFinish) return;
    // Fire as soon as we have a colour or hardware preference
    const hasModifier =
      hardware != null && hardware !== "Not sure";
    if (!hasModifier && finishIdx == null) return;

    if (previewTimer.current) clearTimeout(previewTimer.current);
    const runId = ++previewAbort.current;
    previewTimer.current = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const { data } = await api.post("/preview-finish", {
          piece_id: id,
          finish_type: finishType,
          finish_name: activeFinish?.name || null,
          finish_hex: activeFinish?.hex || null,
          sheen: null,
          // Treat "Not sure" as "no preference" — don't send to AI
          hardware: hardware && hardware !== "Not sure" ? hardware : null,
        });
        if (runId === previewAbort.current) {
          setPreviewUrl(data.url);
          setShowPreview(true);
        }
      } catch (err) {
        if (runId === previewAbort.current) {
          toast.error(
            err?.response?.data?.detail ||
              "Preview couldn't be generated — try again",
          );
        }
      } finally {
        if (runId === previewAbort.current) setPreviewLoading(false);
      }
    }, 500);
    return () => {
      if (previewTimer.current) clearTimeout(previewTimer.current);
    };
  }, [id, finishType, activeFinish, finishIdx, hardware]);

  const goBack = () => {
    // If they're on hardware (step 2) and chose "original", jump back to step 0 (skip the colour step)
    if (step === 2 && finishType === "original") {
      setStep(0);
      return;
    }
    setStep((s) => Math.max(0, s - 1));
  };

  // Skipping forward auto-applies defaults so the customer never gets stuck
  const applyDefaultsUpTo = (target) => {
    // Default finish type
    let nextFinishType = finishType;
    if (target >= 1 && !nextFinishType) {
      nextFinishType = "original";
      setFinishType("original");
      setFinishIdx(null);
    }
    // Default colour — only if they chose a real finish but didn't pick a swatch
    if (target >= 2 && nextFinishType !== "original" && finishIdx == null) {
      const list =
        nextFinishType === "stain" ? palette.stains : palette.paints;
      if (list && list.length > 0) {
        setFinishIdx(0);
      }
    }
    // Default hardware
    if (target >= 3 && !hardware) {
      const keepOriginal = (palette.hardware || []).find(
        (h) => /keep original|no change|original|not sure/i.test(h.name),
      );
      if (keepOriginal) setHardware(keepOriginal.name);
      else if ((palette.hardware || []).length > 0)
        setHardware(palette.hardware[0].name);
    }
  };

  const goTo = (target) => {
    if (target < 0 || target > STEPS.length - 1) return;
    if (target > step) applyDefaultsUpTo(target);
    setStep(target);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_email) {
      toast.error("Please share your name and email");
      return;
    }
    const isOriginal = finishType === "original";
    if ((!isOriginal && !activeFinish) || !hardware) {
      toast.error("Please complete every step");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/quotes", {
        furniture_id: item.id,
        furniture_title: item.title,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone || null,
        finish_type: finishType,
        finish_name: isOriginal ? "Keep Original Finish" : activeFinish.name,
        finish_hex: isOriginal ? "#c19a6b" : activeFinish.hex,
        hardware,
        final_price: currentPrice ?? null,
        message: form.message || null,
      });
      toast.success("Request sent — we'll email you within 48 hours.");
      trackEvent("quote_submit", { piece_id: id });
      // Meta Pixel: fire the Lead event so this counts as a conversion
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        try {
          window.fbq("track", "Lead", {
            content_name: item.title,
            content_ids: [item.code || item.id],
            currency: "CAD",
            value: currentPrice ?? 0,
            event_id: `lead-secure-${item.id}-${Date.now()}`,
          });
        } catch {
          /* never block the success flow if pixel fails */
        }
      }
      setStep(STEPS.length); // show thank-you screen
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  // Lower-commitment CTA — saves the design and emails the customer a recap
  const [savingDesign, setSavingDesign] = useState(false);
  const [savedDesign, setSavedDesign] = useState(false);

  const saveDesign = async () => {
    if (!form.customer_name || !form.customer_email) {
      toast.error("Add your name and email so we can save your design");
      return;
    }
    const isOriginal = finishType === "original";
    if ((!isOriginal && !activeFinish) || !hardware) {
      toast.error("Pick a finish & hardware first");
      return;
    }
    setSavingDesign(true);
    try {
      await api.post("/saved-designs", {
        furniture_id: item.id,
        furniture_title: item.title,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        finish_type: finishType,
        finish_name: isOriginal ? "Keep Original Finish" : activeFinish.name,
        finish_hex: isOriginal ? "#c19a6b" : activeFinish.hex,
        hardware,
        estimated_price: currentPrice ?? null,
      });
      toast.success("Your design has been saved and emailed to you.");
      setSavedDesign(true);
      trackEvent("design_saved", { piece_id: id });
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        try {
          window.fbq("track", "Lead", {
            content_name: item.title,
            content_ids: [item.code || item.id],
            currency: "CAD",
            value: currentPrice ?? 0,
            event_id: `lead-save-${item.id}-${Date.now()}`,
          });
        } catch {
          /* ignore pixel failure */
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save design");
    } finally {
      setSavingDesign(false);
    }
  };

  // (Referral share lives in a homepage popup now — removed from the wizard)

  if (!item) {
    return (
      <div className="bg-white min-h-screen">
        <ArkHeader />
        <div className="py-24 text-center text-[var(--ark-muted)]">
          Loading piece…
        </div>
      </div>
    );
  }

  const done = step >= STEPS.length;

  return (
    <div data-testid="piece-detail-page" className="bg-white text-black">
      <ArkHeader />

      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-10">
        <Link
          to="/"
          data-testid="back-to-collection"
          className="text-[0.72rem] uppercase tracking-[0.18em] text-[var(--ark-muted)] hover:text-[var(--ark-accent)] transition-colors"
        >
          ← Back to Collection
        </Link>
      </div>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-8 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        {/* LEFT: piece image + summary */}
        <div className="lg:col-span-6 lg:sticky lg:top-8 h-fit">
          {(() => {
            const gallery =
              item.images && item.images.length > 0
                ? item.images
                : item.image_url
                  ? [item.image_url]
                  : [];
            const current = gallery[activeImage] || gallery[0];
            const displayUrl =
              showPreview && previewUrl ? previewUrl : current;
            return (
              <>
                <div className="ark-grain aspect-[4/5] bg-[var(--ark-bone)] overflow-hidden relative">
                  <img
                    src={resolveImage(displayUrl)}
                    alt={item.title}
                    data-testid="piece-image"
                    className="h-full w-full object-cover transition-opacity duration-500"
                  />
                  {previewLoading && (
                    <div
                      data-testid="preview-loading"
                      className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center text-white gap-3"
                    >
                      <Loader2 size={28} className="animate-spin" />
                      <div className="text-[0.68rem] uppercase tracking-[0.22em]">
                        Refinishing your piece…
                      </div>
                      <div className="text-[0.6rem] text-white/70 max-w-[220px] text-center">
                        AI preview takes ~15 seconds the first time
                      </div>
                    </div>
                  )}
                  {previewUrl && !previewLoading && (
                    <button
                      type="button"
                      onClick={() => setShowPreview((v) => !v)}
                      data-testid="preview-toggle"
                      className="absolute top-3 right-3 bg-black/80 text-white text-[0.62rem] uppercase tracking-[0.18em] px-3 py-2 inline-flex items-center gap-2 hover:bg-[var(--ark-accent)] transition-colors"
                    >
                      <Sparkles size={12} />
                      {showPreview ? "See original" : "See your finish"}
                    </button>
                  )}
                  {showPreview && previewUrl && !previewLoading && (
                    <div className="absolute bottom-3 left-3 bg-[var(--ark-accent)] text-white text-[0.55rem] uppercase tracking-[0.18em] px-2 py-1 inline-flex items-center gap-1.5">
                      <Sparkles size={10} />
                      AI Preview
                    </div>
                  )}
                </div>
                {gallery.length > 1 && !showPreview && (
                  <div
                    data-testid="piece-thumbnails"
                    className="mt-4 grid grid-cols-5 gap-2"
                  >
                    {gallery.map((url, i) => (
                      <button
                        key={`${url}-${i}`}
                        type="button"
                        data-testid={`piece-thumbnail-${i}`}
                        onClick={() => setActiveImage(i)}
                        className={`aspect-[4/5] overflow-hidden border transition-all ${
                          i === activeImage
                            ? "border-[var(--ark-accent)] opacity-100"
                            : "border-[var(--ark-border)] opacity-60 hover:opacity-100"
                        }`}
                      >
                        <img
                          src={resolveImage(url)}
                          alt={`${item.title} ${i + 1}`}
                          className="h-full w-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
          <div className="mt-5">
            <span className="text-[0.72rem] uppercase tracking-[0.28em] text-[var(--ark-accent)]">
              One-of-one · ARK Collection
            </span>
            <h1
              data-testid="piece-title"
              className="font-display font-bold text-3xl md:text-4xl tracking-tight mt-2"
            >
              {item.title}
            </h1>
            {item.code && (
              <div
                data-testid="piece-code"
                className="mt-2 inline-flex items-center gap-2 text-[0.62rem] uppercase tracking-[0.22em] text-[var(--ark-muted)] font-mono"
              >
                Ref<span className="text-black tracking-[0.18em]">{item.code}</span>
              </div>
            )}
            {item.dimensions && (
              <p className="text-[0.72rem] uppercase tracking-[0.18em] text-[var(--ark-muted)] mt-2">
                {item.dimensions}
              </p>
            )}
            {/* Price intentionally hidden here — only revealed on the final wizard step */}
            <p
              data-testid="piece-description"
              className="mt-4 text-sm text-[var(--ark-muted)] leading-relaxed"
            >
              {item.description}
            </p>
          </div>
        </div>

        {/* RIGHT: wizard */}
        <div className="lg:col-span-6">
          {/* Step indicator */}
          {!done && (
            <>
              <div
                data-testid="wizard-hint"
                className="inline-flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.18em] text-[var(--ark-accent)] mb-5"
              >
                <Sparkles size={11} />
                The photo updates as you choose
              </div>
              <div
                data-testid="wizard-steps"
                className="flex items-center justify-between gap-2 mb-10"
              >
              {STEPS.map((s, i) => (
                <div
                  key={s.k}
                  className="flex items-center flex-1 gap-2 min-w-0"
                >
                  <div
                    data-testid={`wizard-step-dot-${i}`}
                    aria-current={i === step ? "step" : undefined}
                    className={`h-7 w-7 rounded-full flex items-center justify-center border text-[0.65rem] font-semibold ${
                      i < step
                        ? "bg-black text-white border-black"
                        : i === step
                          ? "border-[var(--ark-accent)] text-[var(--ark-accent)]"
                          : "border-[var(--ark-border)] text-[var(--ark-muted)]"
                    }`}
                  >
                    {i < step ? <Check size={12} /> : s.n}
                  </div>
                  {i < STEPS.length - 1 && (
                    <div
                      className={`h-px flex-1 ${i < step ? "bg-black" : "bg-[var(--ark-border)]"}`}
                    />
                  )}
                </div>
              ))}
              </div>
            </>
          )}

          {/* Step content */}
          {!done && (
            <div className="min-h-[360px]" data-testid={`step-${STEPS[step].k}`}>
              <span className="text-[0.72rem] uppercase tracking-[0.28em] text-[var(--ark-accent)]">
                Step {STEPS[step].n}
              </span>
              <h2 className="font-display text-3xl md:text-4xl tracking-tight mt-2 mb-2">
                {STEPS[step].l}
              </h2>
              {step < 3 && (
                <p
                  data-testid="step-hint"
                  className="text-sm text-[var(--ark-muted)] mb-8"
                >
                  Tap an option to preview it, then click Next to continue.
                </p>
              )}

              {step === 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {[
                    {
                      k: "stain",
                      t: "Stain",
                      d: "Let the wood grain show through. Warm, natural, timeless.",
                    },
                    {
                      k: "paint",
                      t: "Paint",
                      d: "Solid colour coverage. Clean, modern or cottage-fresh.",
                    },
                    {
                      k: "original",
                      t: "Keep Original",
                      d: "Preserve the existing finish. Character intact, lightly restored.",
                    },
                  ].map((opt) => {
                    const active = finishType === opt.k;
                    return (
                      <button
                        key={opt.k}
                        type="button"
                        data-testid={`choose-${opt.k}`}
                        onClick={() => {
                          setFinishType(opt.k);
                          setFinishIdx(null);
                        }}
                        className={`text-left p-8 border-2 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${
                          active
                            ? "bg-[var(--ark-accent)] border-[var(--ark-accent)] text-white ring-2 ring-offset-2 ring-[var(--ark-accent)] shadow-md"
                            : "bg-black border-black text-white hover:scale-[1.03]"
                        }`}
                      >
                        <div className="font-display text-2xl mb-1">{opt.t}</div>
                        <div className={`text-sm leading-relaxed ${active ? "text-white/90" : "text-white/70"}`}>
                          {opt.d}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {step === 1 && (
                <div data-testid="swatch-grid" className="grid grid-cols-3 sm:grid-cols-4 gap-y-8 gap-x-5">
                  {activeList.map((s, i) => (
                    <Swatch
                      key={`${finishType}-${s.name}`}
                      name={s.name}
                      hex={s.hex}
                      active={finishIdx === i}
                      onClick={() => setFinishIdx(i)}
                      testid={`swatch-${finishType}-${i}`}
                    />
                  ))}
                </div>
              )}

              {step === 2 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {palette.hardware.map((h) => {
                    const active = hardware === h.name;
                    return (
                      <button
                        key={h.name}
                        type="button"
                        data-testid={`choose-hardware-${h.name.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => setHardware(h.name)}
                        className={`text-left p-6 border-2 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${
                          active
                            ? "bg-[var(--ark-accent)] border-[var(--ark-accent)] text-white ring-2 ring-offset-2 ring-[var(--ark-accent)] shadow-md"
                            : "bg-black border-black text-white hover:scale-[1.03]"
                        }`}
                      >
                        <div className="font-display text-xl">{h.name}</div>
                        {h.description && (
                          <div className={`text-xs mt-1 ${active ? "text-white/90" : "text-white/70"}`}>
                            {h.description}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {step === 3 && (
                <form
                  data-testid="quote-form"
                  onSubmit={submit}
                  className="space-y-6"
                >
                  {/* Live price */}
                  {currentPrice != null && (
                    <div
                      data-testid="final-price"
                      className="border border-[var(--ark-accent)] bg-[var(--ark-bone)] px-6 py-5"
                    >
                      <div className="text-[0.62rem] uppercase tracking-[0.22em] text-[var(--ark-accent)]">
                        Your price
                      </div>
                      <div className="font-display text-4xl md:text-5xl mt-1 inline-flex items-baseline gap-2">
                        <span>{formatPrice(currentPrice)}</span>
                        <span className="text-sm tracking-[0.18em] uppercase text-[var(--ark-muted)]">
                          + HST
                        </span>
                      </div>
                      <p className="text-sm text-[var(--ark-muted)] mt-2 leading-relaxed">
                        This piece is one-of-a-kind. Enter your details below to
                        secure it.
                      </p>
                    </div>
                  )}
                  <div
                    data-testid="scarcity-banner"
                    className="flex items-center gap-3 border border-black bg-black text-white px-4 py-3"
                  >
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--ark-accent)] opacity-75 animate-ping" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--ark-accent)]" />
                    </span>
                    <span className="text-[0.72rem] uppercase tracking-[0.2em]">
                      Only 1 available — don&apos;t miss this piece
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="ark-label" htmlFor="cn">
                        Name
                      </label>
                      <input
                        id="cn"
                        data-testid="quote-name"
                        className="ark-input"
                        value={form.customer_name}
                        onChange={(e) =>
                          setForm({ ...form, customer_name: e.target.value })
                        }
                        placeholder="First name"
                        required
                      />
                    </div>
                    <div>
                      <label className="ark-label" htmlFor="ce">
                        Email
                      </label>
                      <input
                        id="ce"
                        data-testid="quote-email"
                        type="email"
                        className="ark-input"
                        value={form.customer_email}
                        onChange={(e) =>
                          setForm({ ...form, customer_email: e.target.value })
                        }
                        placeholder="Email to send your price"
                        required
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="ark-label" htmlFor="cp">
                        Phone number
                      </label>
                      <input
                        id="cp"
                        data-testid="quote-phone"
                        className="ark-input"
                        value={form.customer_phone}
                        onChange={(e) =>
                          setForm({ ...form, customer_phone: e.target.value })
                        }
                        placeholder="(xxx) xxx-xxxx"
                      />
                    </div>
                  </div>
                  {/* Summary */}
                  <div
                    data-testid="wizard-summary"
                    className="border border-[var(--ark-accent)] p-5 bg-[var(--ark-bone)] shadow-sm"
                  >
                    <div className="text-[0.65rem] uppercase tracking-[0.18em] text-[var(--ark-accent)] mb-3">
                      Your custom piece
                    </div>
                    <div className="flex items-center gap-3 flex-wrap text-sm">
                      {finishType === "original" ? (
                        <span>
                          <strong>Keep Original Finish</strong>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-2">
                          <span
                            className="h-4 w-4 rounded-full border border-white"
                            style={{ background: activeFinish?.hex }}
                          />
                          <strong>{activeFinish?.name}</strong>
                          <span className="text-[var(--ark-muted)]">
                            ({finishType})
                          </span>
                        </span>
                      )}
                      <span className="text-[var(--ark-muted)]">·</span>
                      <span>{hardware}</span>
                      {currentPrice != null && (
                        <>
                          <span className="text-[var(--ark-muted)]">·</span>
                          <strong className="text-[var(--ark-accent)]">
                            {formatPrice(currentPrice)}
                          </strong>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="space-y-3">
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="quote-submit"
                      className="ark-btn w-full disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {submitting ? "Sending…" : "Secure Your Piece"}
                    </button>
                    <button
                      type="button"
                      onClick={saveDesign}
                      disabled={savingDesign || savedDesign}
                      data-testid="save-design-btn"
                      className="w-full text-[0.72rem] uppercase tracking-[0.18em] py-3 border border-black bg-white text-black hover:bg-black hover:text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {savedDesign
                        ? "Saved — check your email"
                        : savingDesign
                          ? "Saving…"
                          : "Save This Design"}
                    </button>
                    <p className="text-xs text-[var(--ark-muted)] text-center leading-relaxed">
                      Big decisions take time. Save your custom design and
                      we&apos;ll email you the details so you can come back to
                      it later.
                    </p>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* Step navigation: Back + Next */}
          {!done && step < 3 && (
            (() => {
              const goNext = () => {
                // Pick the next visible step (skipping colour when "Keep Original" is chosen)
                let target = step + 1;
                if (step === 0 && finishType === "original") target = 2;
                applyDefaultsUpTo(target);
                setStep(target);
              };
              return (
                <div className="mt-10 flex items-center justify-between gap-4">
                  {step > 0 ? (
                    <button
                      type="button"
                      onClick={goBack}
                      data-testid="wizard-back"
                      className="inline-flex items-center gap-2 text-[0.72rem] uppercase tracking-[0.18em] text-[var(--ark-muted)] hover:text-black"
                    >
                      <ArrowLeft size={14} /> Back
                    </button>
                  ) : (
                    <span />
                  )}
                  <button
                    type="button"
                    onClick={goNext}
                    data-testid="wizard-next"
                    className="ark-btn inline-flex items-center gap-2"
                  >
                    Next <ArrowRight size={14} />
                  </button>
                </div>
              );
            })()
          )}
          {!done && step === 3 && (
            <div className="mt-6">
              <button
                type="button"
                onClick={goBack}
                data-testid="wizard-back"
                className="inline-flex items-center gap-2 text-[0.72rem] uppercase tracking-[0.18em] text-[var(--ark-muted)] hover:text-black"
              >
                <ArrowLeft size={14} /> Back
              </button>
            </div>
          )}

          {done && (
            <div
              data-testid="wizard-done"
              className="border border-[var(--ark-border)] p-10 text-center reveal"
            >
              <div className="h-12 w-12 rounded-full bg-[var(--ark-accent)] text-white mx-auto flex items-center justify-center">
                <Check size={20} />
              </div>
              <h2 className="font-display text-3xl mt-5">Request received.</h2>
              <p className="text-[var(--ark-muted)] mt-2 max-w-sm mx-auto">
                We&apos;ve emailed our design team at design@arkfurniture.ca.
                You&apos;ll hear back within 48 hours.
              </p>
              {item.code && (
                <div className="mt-5 inline-flex items-center gap-2 text-[0.62rem] uppercase tracking-[0.22em] text-[var(--ark-muted)] font-mono">
                  Your reference
                  <span className="text-black tracking-[0.18em] border border-[var(--ark-border)] px-2 py-1">
                    {item.code}
                  </span>
                </div>
              )}
              <Link to="/" className="ark-btn mt-8 inline-flex">
                Back to collection
              </Link>
            </div>
          )}
        </div>
      </section>

      <ArkFooter />
    </div>
  );
}
